import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type AriaRole,
  type ReactNode,
  type Ref,
  type MutableRefObject,
} from 'react'
import { color } from '../tokens'
import './KwOverlayScroll.css'

type KwOverlayScrollProps = {
  role?: AriaRole
  tabIndex?: number
  /** Content-sized menus/dialogs grow naturally and shrink within their parent's limit. */
  sizing?: 'fill' | 'content'
  children: ReactNode
  style?: CSSProperties
  className?: string
  /** Accessible name for page-level or otherwise meaningful scroll regions. */
  ariaLabel?: string
  /** Hide delay after last scroll (ms). Default 900 — Kontakt-Hub-Parität. */
  hideDelayMs?: number
  /** Compact composers can retain scrolling without a visible track or reserved gutter. */
  showIndicator?: boolean
  /** Ref auf den scrollbaren Inner-Container (z. B. IntersectionObserver). */
  scrollerRef?: Ref<HTMLDivElement>
  /**
   * Scroll-Achse für Overlay-Thumb.
   * `y` = Person-Hub / Detail (default). `x` = z. B. Kanban horizontal.
   */
  axis?: 'y' | 'x'
  /** Zusätzliches Overflow auf der anderen Achse (Kanban: y mitlesen). Default: hidden. */
  crossAxis?: 'hidden' | 'auto'
  /**
   * Bei `axis="x"`: Leere Flächen halten+ziehen → pan; Mausrad → horizontal.
   * Interaktive Kinder (button, [draggable]) bleiben unangetastet.
   */
  panDrag?: boolean
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return
  if (typeof ref === 'function') ref(value)
  else (ref as MutableRefObject<T | null>).current = value
}

function isPanBlockedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true
  return Boolean(
    target.closest(
      'button, a[href], input, textarea, select, [role="button"], [draggable="true"], [data-no-pan]',
    ),
  )
}

/**
 * Overlay scroller — 3px thumb, native scrollbar hidden.
 * Pattern from contacts person-hub preview (PO 2026-09-26/27).
 * Native scrollbar hiding ships with the atom so App, Storybook and embedded
 * surfaces render the same 3px overlay thumb.
 *
 * @atom Atom
 */
export function KwOverlayScroll({
  sizing = 'fill',
  children,
  style,
  className,
  ariaLabel,
  role:scrollRole,
  tabIndex=0,
  hideDelayMs = 900,
  showIndicator = true,
  scrollerRef,
  axis = 'y',
  crossAxis = 'hidden',
  panDrag = false,
}: KwOverlayScrollProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [scrolling, setScrolling] = useState(false)
  const [trackHover, setTrackHover] = useState(false)
  const [panning, setPanning] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const horizontal = axis === 'x'
  const enablePan = panDrag && horizontal

  useEffect(() => {
    const scroller = scrollRef.current
    const thumb = thumbRef.current
    if (!scroller || !thumb) return

    const markActive = () => {
      setScrolling(true)
      if (hideTimer.current) clearTimeout(hideTimer.current)
      hideTimer.current = setTimeout(() => setScrolling(false), hideDelayMs)
    }

    const syncThumb = () => {
      if (horizontal) {
        const { scrollLeft, scrollWidth, clientWidth } = scroller
        const track = Math.max(clientWidth - 12, 1)
        const canScroll = scrollWidth > clientWidth + 1
        setOverflows(canScroll)
        if (!canScroll) {
          thumb.style.width = '0px'
          return
        }
        const ratio = clientWidth / scrollWidth
        const w = Math.max(36, Math.round(track * ratio))
        const maxLeft = track - w
        const left =
          scrollWidth === clientWidth
            ? 0
            : maxLeft * (scrollLeft / (scrollWidth - clientWidth))
        thumb.style.width = `${w}px`
        thumb.style.height = '3px'
        thumb.style.top = '4px'
        thumb.style.transform = `translateX(${Math.round(left)}px)`
        return
      }

      const { scrollTop, scrollHeight, clientHeight } = scroller
      const track = Math.max(clientHeight - 12, 1)
      setOverflows(scrollHeight > clientHeight + 1)
      if (scrollHeight <= clientHeight + 1) {
        thumb.style.height = '0px'
        return
      }
      const ratio = clientHeight / scrollHeight
      const h = Math.max(28, Math.round(track * ratio))
      const maxTop = track - h
      const top = maxTop * (scrollTop / (scrollHeight - clientHeight))
      thumb.style.height = `${h}px`
      thumb.style.width = '100%'
      thumb.style.top = '0'
      thumb.style.transform = `translateY(${Math.round(top)}px)`
    }

    const onScroll = () => {
      markActive()
      syncThumb()
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', syncThumb, { passive: true })
    syncThumb()

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => syncThumb())
        : null
    ro?.observe(scroller)

    // —— Wheel → horizontal (axis=x); Spalten-Listen behalten Vertikal-Scroll ——
    const onWheel = (e: WheelEvent) => {
      if (!horizontal) return
      const { scrollWidth, clientWidth } = scroller
      if (scrollWidth <= clientWidth + 1) return

      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        // Trackpad horizontal — Browser scrollt den Scroller bereits
        markActive()
        return
      }

      if (!e.shiftKey) {
        const list =
          e.target instanceof Element ? e.target.closest('[role="list"]') : null
        if (list instanceof HTMLElement) {
          const canUp = list.scrollTop > 0
          const canDown = list.scrollTop + list.clientHeight < list.scrollHeight - 1
          if ((e.deltaY < 0 && canUp) || (e.deltaY > 0 && canDown)) return
        }
      }

      const prev = scroller.scrollLeft
      scroller.scrollLeft += e.deltaY
      if (scroller.scrollLeft !== prev) {
        e.preventDefault()
        markActive()
      }
    }
    scroller.addEventListener('wheel', onWheel, { passive: false })

    // —— Hold + drag on empty surface ——
    let panPointerId: number | null = null
    let panStartX = 0
    let panStartScroll = 0
    let panMoved = false

    const onPointerDown = (e: PointerEvent) => {
      if (!enablePan || e.button !== 0) return
      if (isPanBlockedTarget(e.target)) return
      panPointerId = e.pointerId
      panStartX = e.clientX
      panStartScroll = scroller.scrollLeft
      panMoved = false
      scroller.setPointerCapture(e.pointerId)
    }

    const onPointerMove = (e: PointerEvent) => {
      if (panPointerId !== e.pointerId) return
      const dx = e.clientX - panStartX
      if (!panMoved && Math.abs(dx) < 4) return
      if (!panMoved) {
        panMoved = true
        setPanning(true)
        markActive()
      }
      scroller.scrollLeft = panStartScroll - dx
      e.preventDefault()
    }

    const endPan = (e: PointerEvent) => {
      if (panPointerId !== e.pointerId) return
      panPointerId = null
      setPanning(false)
      try {
        scroller.releasePointerCapture(e.pointerId)
      } catch {
        /* already released */
      }
    }

    if (enablePan) {
      scroller.addEventListener('pointerdown', onPointerDown)
      scroller.addEventListener('pointermove', onPointerMove)
      scroller.addEventListener('pointerup', endPan)
      scroller.addEventListener('pointercancel', endPan)
    }

    // —— Thumb / track drag ——
    const track = trackRef.current
    let thumbPointerId: number | null = null

    const scrollFromTrackX = (clientX: number) => {
      if (!track) return
      const rect = track.getBoundingClientRect()
      const { scrollWidth, clientWidth } = scroller
      const trackW = Math.max(rect.width, 1)
      const thumbW = Math.max(36, Math.round(trackW * (clientWidth / scrollWidth)))
      const maxScroll = scrollWidth - clientWidth
      const maxThumb = Math.max(trackW - thumbW, 1)
      const x = clientX - rect.left - thumbW / 2
      const ratio = Math.min(1, Math.max(0, x / maxThumb))
      scroller.scrollLeft = ratio * maxScroll
      markActive()
    }

    const onThumbDown = (e: PointerEvent) => {
      if (!horizontal || e.button !== 0) return
      e.preventDefault()
      e.stopPropagation()
      thumbPointerId = e.pointerId
      track?.setPointerCapture(e.pointerId)
      scrollFromTrackX(e.clientX)
      setPanning(true)
    }

    const onThumbMove = (e: PointerEvent) => {
      if (thumbPointerId !== e.pointerId) return
      scrollFromTrackX(e.clientX)
    }

    const onThumbUp = (e: PointerEvent) => {
      if (thumbPointerId !== e.pointerId) return
      thumbPointerId = null
      setPanning(false)
      try {
        track?.releasePointerCapture(e.pointerId)
      } catch {
        /* */
      }
    }

    if (horizontal && track) {
      track.addEventListener('pointerdown', onThumbDown)
      track.addEventListener('pointermove', onThumbMove)
      track.addEventListener('pointerup', onThumbUp)
      track.addEventListener('pointercancel', onThumbUp)
    }

    return () => {
      scroller.removeEventListener('scroll', onScroll)
      scroller.removeEventListener('wheel', onWheel)
      window.removeEventListener('resize', syncThumb)
      ro?.disconnect()
      if (hideTimer.current) clearTimeout(hideTimer.current)
      if (enablePan) {
        scroller.removeEventListener('pointerdown', onPointerDown)
        scroller.removeEventListener('pointermove', onPointerMove)
        scroller.removeEventListener('pointerup', endPan)
        scroller.removeEventListener('pointercancel', endPan)
      }
      if (horizontal && track) {
        track.removeEventListener('pointerdown', onThumbDown)
        track.removeEventListener('pointermove', onThumbMove)
        track.removeEventListener('pointerup', onThumbUp)
        track.removeEventListener('pointercancel', onThumbUp)
      }
    }
  }, [hideDelayMs, horizontal, enablePan])

  const showThumb = overflows && (scrolling || trackHover || panning)
  // Horizontal: faint idle grabber when content overflows (discoverable, not fat)
  const thumbOpacity = !overflows
    ? 0
    : showThumb
      ? 1
      : horizontal
        ? 0.28
        : 0

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        minHeight: 0,
        overflow: 'hidden',
        ...(sizing === 'content' ? { display: 'flex', flexDirection: 'column', minWidth: 0 } as const : {}),
        ...style,
      }}
    >
      <div
        ref={(el) => {
          scrollRef.current = el
          assignRef(scrollerRef, el)
        }}
        className="kw-overlay-scroll"
        tabIndex={tabIndex}
        role={scrollRole??(ariaLabel ? 'region' : undefined)}
        aria-label={ariaLabel}
        style={{
          position: sizing === 'content' ? 'relative' : 'absolute',
          inset: sizing === 'content' ? undefined : 0,
          ...(sizing === 'content' ? { minHeight: 0, minWidth: 0, maxHeight: 'inherit', maxWidth: '100%', flex: '0 1 auto' } : {}),
          overflowX: horizontal ? 'auto' : crossAxis,
          overflowY: horizontal ? crossAxis : 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: horizontal ? 'pan-x pan-y' : 'pan-y',
          overscrollBehavior: 'contain',
          cursor: panning ? 'grabbing' : undefined,
          paddingBottom: horizontal && overflows && showIndicator ? 10 : undefined,
        }}
      >
        {children}
      </div>
      <div
        ref={trackRef}
        aria-hidden
        onMouseEnter={() => setTrackHover(true)}
        onMouseLeave={() => setTrackHover(false)}
        style={
          horizontal
            ? {
                position: 'absolute',
                left: 6,
                visibility: showIndicator ? 'visible' : 'hidden',
                right: 6,
                bottom: 0,
                height: 12,
                pointerEvents: overflows ? 'auto' : 'none',
                zIndex: 3,
                cursor: overflows ? (panning ? 'grabbing' : 'grab') : 'default',
              }
            : {
                position: 'absolute',
                top: 6,
                visibility: showIndicator ? 'visible' : 'hidden',
                right: 2,
                bottom: 6,
                width: 3,
                pointerEvents: 'none',
                zIndex: 3,
              }
        }
      >
        <div
          ref={thumbRef}
          style={{
            position: 'absolute',
            left: 0,
            top: horizontal ? 4 : 0,
            ...(horizontal
              ? { height: 3, minWidth: 36 }
              : { width: '100%', minHeight: 28 }),
            borderRadius: 999,
            background: color.whiteAlpha(horizontal ? 0.42 : 0.32),
            opacity: showIndicator ? thumbOpacity : 0,
            transition: panning ? undefined : 'opacity 0.18s ease',
            boxShadow: horizontal ? `0 0 0 1px ${color.blackAlpha(0.35)}` : undefined,
          }}
        />
      </div>
    </div>
  )
}
